/**
 * Spec 216 PR2 — accessible tooltip wrapper.
 *
 * Thin convenience wrapper over `@radix-ui/react-tooltip`:
 *   - One-line usage: `<Tooltip label="Cerrar">{trigger}</Tooltip>`.
 *   - 250 ms delay matches the OS-level hover-tooltip cadence and
 *     prevents the "every cursor sweep flashes a balloon" UX that
 *     instant tooltips create on dense toolbars.
 *   - `<TooltipProvider>` is mounted once at the App shell level so
 *     individual call-sites stay flat and the open-state book-keeping
 *     batches under a single timer (Radix recommendation).
 *
 * Tokens used: `--c-text-dark`, `--c-bg`, `--shadow-md`, `--ls-tight`.
 * No new tokens — strict zero-add per PR2 contract.
 */
import { type ReactElement } from 'react';
import * as RadixTooltip from '@radix-ui/react-tooltip';

import './Tooltip.css';

interface TooltipProps {
  label: string;
  children: ReactElement;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  /**
   * Disable the tooltip without unmounting the trigger — useful for
   * states where the same icon button is permanently disabled and the
   * label would lie about an inactive control.
   */
  disabled?: boolean;
}

export function Tooltip({ label, children, side = 'bottom', align = 'center', disabled }: TooltipProps) {
  if (disabled) return children;
  return (
    <RadixTooltip.Root>
      <RadixTooltip.Trigger asChild>{children}</RadixTooltip.Trigger>
      <RadixTooltip.Portal>
        <RadixTooltip.Content
          className="ui-tooltip-content"
          side={side}
          align={align}
          sideOffset={6}
          data-testid="tooltip-content"
        >
          {label}
          <RadixTooltip.Arrow className="ui-tooltip-arrow" />
        </RadixTooltip.Content>
      </RadixTooltip.Portal>
    </RadixTooltip.Root>
  );
}

/**
 * Mount once at the top of the tree (AppShell). Delay 250 ms matches
 * macOS / Windows native tooltips. `skipDelayDuration` lets a user
 * who's already triggered one tooltip move their cursor across other
 * triggers within 400 ms without re-paying the delay (Radix default).
 */
export const TooltipProvider = (props: { children: ReactElement }) => (
  <RadixTooltip.Provider delayDuration={250} skipDelayDuration={400}>
    {props.children}
  </RadixTooltip.Provider>
);
